import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";

const ImageResizer = () => {
  const [image, setImage] = useState(null);
  const [resizedImage, setResizedImage] = useState(null);
  const [dimensions, setDimensions] = useState("3240x1080");

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      setImage(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleResize = () => {
    const [width, height] = dimensions.split("x").map(Number);
    const img = new Image();
    img.src = image;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(img, 0, 0, width, height);

      setResizedImage(canvas.toDataURL());
    };
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = resizedImage;
    link.download = "resized-image.png";
    link.click();
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: 2,
        padding: { xs: "30px", sm: "30px", md: "30px 60px" },
        boxSizing: "border-box",
        backgroundColor: "#f9f9f9",
        boxShadow: "0px 0px 30px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{ textAlign: "center", fontWeight: "bold", mb: 4 }}
      >
        Resize Image Dimensions
      </Typography>

      <Grid container spacing={5} alignItems="center">
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              textAlign: "center",
              border: "2px dashed #ccc",
              borderRadius: 2,
              padding: "20px",
              backgroundColor: "#ffffff",
              boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.05)",
              height: image ? { xs: "auto", md: "500px" } : "200px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Upload Image
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: image ? "calc(100% - 80px)" : "100px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: "2px solid #ccc",
                borderRadius: 2,
                mb: 2,
                overflow: "hidden",
                padding: "10px",
              }}
            >
              {image && (
                <img
                  src={image}
                  alt="Uploaded"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
              )}
            </Box>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<UploadFileIcon />}
              component="label"
              sx={{ borderRadius: "8px" }}
            >
              Choose File
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageUpload}
              />
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              textAlign: "center",
              border: "2px dashed #ccc",
              borderRadius: 2,
              padding: "20px",
              backgroundColor: "#ffffff",
              boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.05)",
              height: resizedImage ? { xs: "auto", md: "500px" } : "200px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Select Dimensions & Resize
            </Typography>
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel id="dimension-select-label">Dimensions</InputLabel>
              <Select
                labelId="dimension-select-label"
                value={dimensions}
                onChange={(e) => setDimensions(e.target.value)}
                label="Dimensions"
              >
                <MenuItem value="3240x1080">3240 x 1080</MenuItem>
                <MenuItem value="3240x2160">3240 x 2160</MenuItem>
                <MenuItem value="3240x3240">3240 x 3240</MenuItem>
                <MenuItem value="3240x4320">3240 x 4320</MenuItem>
                <MenuItem value="3240x5400">3240 x 5400</MenuItem>
                <MenuItem value="3240x6480">3240 x 6480</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="secondary"
              sx={{ borderRadius: "8px", mb: 2 }}
              onClick={handleResize}
              disabled={!image}
            >
              Resize Image
            </Button>
            <Box
              sx={{
                width: "100%",
                height: resizedImage ? "calc(100% - 80px)" : "100px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: "2px solid #ccc",
                borderRadius: 2,
                mb: 2,
                overflow: "hidden",
                padding: "10px",
              }}
            >
              {resizedImage && (
                <img
                  src={resizedImage}
                  alt="Resized"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
              )}
            </Box>
            {resizedImage && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: "8px" }}
                onClick={handleDownload}
              >
                Download Image
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ImageResizer;
